/**
 * Heavy `POLYLINE` → the ONE **drawn chain** projection every curve command in
 * this package reads: "which records does this polyline actually draw, in what
 * order, with which per-span bulge, and is the run closed?"
 *
 * ## Why this is a leaf of its own
 *
 * `./heavyPolylineTrim.ts` introduced this projection for TRIM / EXTEND
 * (@codex PR #785 round 4). @codex PR #785 round 14 then needed the IDENTICAL
 * answer for two more commands — `geometry/offset-entity` and
 * `geometry/create-arraypath`, whose eligibility rules are their own
 * (`./heavyPolylineCurveRefusal.ts`) but whose GEOMETRY question is word for
 * word TRIM's. Copying the twelve-line walk into a second module would have
 * put "what does a heavy polyline draw?" in two places free to drift — the
 * clone `npm run dup:check` gates on and CLAUDE.md ПРИОРИТЕТ #1 tells us to
 * EXTRACT. So the walk moved down here and `heavyPolylineTrim.ts`'s
 * `heavyPolylineChain` became its role-gated wrapper; nothing about TRIM's
 * behaviour changed.
 *
 * ## Where the record→run rule itself comes from
 *
 * Not from here: {@link heavyPolylineRuns} (`@dxf-ai-editor/cad-core`) is the
 * established single truth for WHICH VERTEX records are visible geometry, in
 * what order, and which spans may bulge — the same one the scene3d render
 * adapter, the picker, the vector-PDF plot and the AI OSNAP route read. This
 * module only flattens its ONE non-mesh run into the `{x, y, bulge}` shape the
 * LWPOLYLINE-oriented helpers of `command-engine` already consume, and carries
 * the record indices alongside so a write-back can preserve each record's own
 * handle, XDATA, extension dictionary, reactors and unmapped groups.
 *
 * ## Coordinates are the records' OWN x/y — the OCS, and deliberately so
 *
 * Every curve command in this package works in raw entity coordinates:
 * `LWPOLYLINE`'s tessellation (`trimExtendIntersection.ts::polylineToPoints`),
 * the ARRAYPATH sampler's walk (`./pathSampling.ts`) and the OFFSET vertex ring
 * (`apps/web/.../state/geometryReducerEdit.ts`) all read `vertex.x` / `vertex.y`
 * directly and leave the entity's group-210 extrusion and elevation alone.
 * Lifting ONLY the heavy polyline here would put it in a different frame from
 * the LWPOLYLINE beside it, and OFFSET — which WRITES this chain back onto the
 * VERTEX records — would then need the inverse (WCS→OCS) map to put its result
 * where the file expects it. A 2D heavy polyline's x/y are OCS coordinates in
 * an ORTHONORMAL basis, so chord lengths, offsets and arc lengths are identical
 * in that plane and in WCS — the same argument `./heavyPolylinePathMetrics.ts`
 * makes for the Length / Area rows. So the chain is the WRITE-BACK frame, and
 * OFFSET's "inverse mapping" is the cheapest one there is: none at all.
 *
 * ## …which is why the frame change belongs to the CONSUMER that leaves it
 *
 * @codex PR #785 round 15, P2 ("Lift heavy ARRAYPATH samples from OCS into
 * WCS"). One consumer does not stay inside the entity: ARRAYPATH places COPIES
 * OF OTHER ENTITIES along this curve, and those live in WCS. Reading the chain
 * as if its numbers were WCS put the copies of an extrusion-`(0,0,-1)` path at
 * `+x` while the path itself is drawn at `-x`. The repair is in
 * `./pathSampleOcsFrame.ts`'s `pathOcsPlane`, which `./pathSampling.ts` calls
 * for ALL four OCS-planar path kinds rather than forking one family: the walk
 * still runs on THIS chain, in this frame, and the finished sample is
 * re-expressed in WCS on the way out. Because an OCS basis is orthonormal the
 * lift is an isometry, so the two orders agree exactly — and this module keeps
 * one walk and one frame, with no second traversal and no bulge-handedness
 * bookkeeping.
 *
 * TRIM / EXTEND, the third consumer, reads the chain and is UNCHANGED by that:
 * it intersects this curve with other entities' equally raw stored coordinates
 * (`trimExtendIntersection.ts` reads no extrusion for any entity type) and
 * writes the survivor back into these same records. A tilted-extrusion drawing
 * is therefore still EDITED in its OCS plane — one limitation for the whole
 * package to lift at once, not a heavy-polyline one.
 */

import {
  DXF_POLYLINE_FLAG,
  polylineSubtype,
  type PolylineEntity,
} from "@dxf-ai-editor/drawing-model";
import { heavyPolylineRuns } from "@dxf-ai-editor/cad-core";

/** One vertex of the flattened chain a heavy polyline presents to a curve command. */
export interface HeavyPolylineChainVertex {
  readonly x: number;
  readonly y: number;
  /** DXF group 42 of the span LEAVING this vertex; always 0 for a 3D polyline. */
  readonly bulge: number;
  // NO width channel (@codex round 5). Groups 40/41 have TWO readings a caller
  // needs — the RESOLVED width the span is drawn at, and whether the record
  // DECLARED it — and this chain could only ever carry one of them. Carrying
  // the raw fields (what it did before) handed the TARGET math `undefined` for
  // every inheriting record, which its `?? 0` then read as a zero-width span.
  // Both readings are now taken at their own source instead: the resolved pair
  // from `drawing-model`'s `heavyPolylineWidths` — the one truth for
  // "record 40/41, else the POLYLINE's own" — and the presence from the VERTEX
  // record itself, in `./heavyPolylineTrimTarget.ts`'s write-back. `indices`
  // below is what lets both be read for the same vertex.
}

/**
 * The heavy polyline's drawn chain, in the shape every LWPOLYLINE-oriented
 * helper in this package already consumes.
 *
 * `indices[k]` is the `entity.vertices` record `vertices[k]` came from, so a
 * write-back can carry each surviving record's handle, XDATA, extension
 * dictionary, reactors and unmapped groups VERBATIM instead of minting a bare
 * replacement (the per-VERTEX loss PR #785 round 1 repaired one level up).
 */
export interface HeavyPolylineDrawnChain {
  readonly vertices: readonly HeavyPolylineChainVertex[];
  readonly closed: boolean;
  readonly indices: readonly number[];
}

/**
 * Group 70 bit 2 | bit 4 — this polyline's record run is the OUTPUT of a
 * fitting pass (curve-fit or spline-fit).
 *
 * The one truth for that question in this package: a command that only READS
 * the run (a TRIM cutting edge, an ARRAYPATH path curve) does not care, while
 * a command that WRITES the run back (a TRIM target, an OFFSET clone) cannot
 * honour it without a polyline fitter this codebase does not have — so each
 * consumer asks this predicate rather than re-decoding group 70.
 */
export function isHeavyPolylineFitted(
  entity: Pick<PolylineEntity, "flags">,
): boolean {
  return (
    (entity.flags &
      (DXF_POLYLINE_FLAG.CURVE_FIT | DXF_POLYLINE_FLAG.SPLINE_FIT)) !==
    0
  );
}

/**
 * The drawn chain of a heavy polyline, or `null` when it does not HAVE one:
 * a polygon / poly-face mesh yields many runs (rows, columns, face edges) and
 * no single traversal, and a run of fewer than two records forms no segment.
 *
 * Total and pure. It applies NO eligibility rule of its own — each command's
 * own refusal predicate (`./heavyPolylineTrim.ts`'s role gate,
 * `./heavyPolylineCurveRefusal.ts`'s OFFSET / ARRAYPATH gate) decides whether
 * the chain may be used at all, because the answers genuinely differ: a 3D
 * polyline is a perfectly good TRIM cutting edge and not a valid OFFSET target.
 */
export function heavyPolylineDrawnChain(
  entity: PolylineEntity,
): HeavyPolylineDrawnChain | null {
  const subtype = polylineSubtype(entity);
  if (subtype === "polygon-mesh" || subtype === "polyface-mesh") return null;
  // A non-mesh subtype always yields EXACTLY ONE run (`heavyPolylineRuns`'s own
  // contract), and the mesh subtypes — the only ones that yield several — left
  // above.
  const run = heavyPolylineRuns(entity)[0];
  if (!run || run.indices.length < 2) return null;
  const vertices = run.indices.map((recordIndex, k) => {
    const record = entity.vertices[recordIndex]!;
    const vertex: HeavyPolylineChainVertex = {
      x: record.location.x,
      y: record.location.y,
      bulge: run.bulges[k] ?? 0,
    };
    return vertex;
  });
  return { vertices, closed: run.closed, indices: run.indices };
}
